import * as THREE from 'three';
import { CROSSFADE_DURATION, IDLE_THRESHOLD, WALK_THRESHOLD } from '../config';
import type { AnimationState } from '../types';

// Animation clip name patterns — supports Kenney, Meshy AI, and Mixamo rigs
const ANIMATION_ALIASES: Record<AnimationState, string[]> = {
  idle: ['Idle', 'idle', 'CharacterArmature|Idle', 'mixamo.com|Idle', 'Armature|Idle'],
  walk: ['Walking', 'Walk', 'walk', 'CharacterArmature|Walk', 'mixamo.com|Walk', 'Armature|Walk'],
  run: ['Running', 'Run', 'run', 'CharacterArmature|Run', 'mixamo.com|Run', 'Armature|Run'],
  jump: ['Jump_Over_Obstacle_2', 'Jump_Run', 'Jump', 'jump', 'CharacterArmature|Jump', 'mixamo.com|Jump'],
  fall: ['Fall_Down', 'Fall', 'fall', 'CharacterArmature|Fall', 'mixamo.com|Fall', 'Jump_with_Arms_Open'],
};

export class AnimationStateMachine {
  private mixer: THREE.AnimationMixer;
  private actions = new Map<AnimationState, THREE.AnimationAction>();
  private currentState: AnimationState = 'idle';
  private currentAction: THREE.AnimationAction | null = null;

  constructor(model: THREE.Group, clips: THREE.AnimationClip[]) {
    this.mixer = new THREE.AnimationMixer(model);

    // Log available clips for debugging
    console.log('Available animation clips:', clips.map((c) => c.name));

    // Match clips to states
    for (const [state, aliases] of Object.entries(ANIMATION_ALIASES)) {
      for (const alias of aliases) {
        const clip = clips.find((c) => c.name === alias);
        if (clip) {
          const action = this.mixer.clipAction(clip);
          this.actions.set(state as AnimationState, action);
          break;
        }
      }
    }

    // Start idle
    const idle = this.actions.get('idle');
    if (idle) {
      idle.play();
      this.currentAction = idle;
    }
  }

  update(delta: number, speed: number, velocityY: number, grounded: boolean) {
    this.mixer.update(delta);

    let targetState: AnimationState;

    if (!grounded && velocityY < -1) {
      targetState = 'fall';
    } else if (!grounded && velocityY > 0) {
      targetState = 'jump';
    } else if (speed > WALK_THRESHOLD) {
      targetState = 'run';
    } else if (speed > IDLE_THRESHOLD) {
      targetState = 'walk';
    } else {
      targetState = 'idle';
    }

    if (targetState !== this.currentState) {
      this.transition(targetState);
    }
  }

  private transition(newState: AnimationState) {
    const nextAction = this.actions.get(newState);
    if (!nextAction) return;

    if (this.currentAction && this.currentAction !== nextAction) {
      this.currentAction.fadeOut(CROSSFADE_DURATION);
    }

    nextAction.reset().fadeIn(CROSSFADE_DURATION).play();

    // One-shot for jump
    if (newState === 'jump') {
      nextAction.setLoop(THREE.LoopOnce, 1);
      nextAction.clampWhenFinished = true;
    } else {
      nextAction.setLoop(THREE.LoopRepeat, Infinity);
    }

    this.currentState = newState;
    this.currentAction = nextAction;
  }

  getCurrentState(): AnimationState {
    return this.currentState;
  }
}
